import { useState, useEffect } from "react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { API_URL } from "../config/api";
import {
  Key,
  Copy,
  Check,
  Plus,
  Trash2,
  Clock,
  Shield,
  Code,
  FileJson,
  Ship,
  RefreshCw,
  Calendar,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff,
  Zap,
  Globe,
  Lock,
  Unlock,
} from "lucide-react";


// Code block component with copy functionality
function CodeBlock({ code, language = "json" }) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="relative group">
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-2 bg-slate-700 hover:bg-slate-600 rounded transition-colors opacity-0 group-hover:opacity-100"
      >
        {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4 text-slate-300" />}
      </button>
    </div>
  );
}

// Expandable section component
function ExpandableSection({ title, icon: Icon, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="h-5 w-5 text-blue-600" />
          <span className="font-semibold text-slate-800">{title}</span>
        </div>
        {isOpen ? <ChevronDown className="h-5 w-5 text-slate-500" /> : <ChevronRight className="h-5 w-5 text-slate-500" />}
      </button>
      {isOpen && <div className="p-4 border-t bg-white">{children}</div>}
    </div>
  );
}

export default function APIsPage() {
  const { token, user } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newKeyName, setNewKeyName] = useState("");
  const [generatedKey, setGeneratedKey] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const [activeTab, setActiveTab] = useState("documentation");
  
  useEffect(() => {
    if (token) {
      fetchApiKeys();
      fetchStats();
    }
  }, [token]);
  
  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`${API_URL}/lcl-status/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeys(response.data);
    } catch (error) {
      console.error("Error fetching API keys:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/lcl-status/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };
  
  const generateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error("Please enter a name for the API key");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API_URL}/lcl-status/api-keys/generate?name=${encodeURIComponent(newKeyName)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGeneratedKey(response.data);
      setNewKeyName("");
      fetchApiKeys();
      toast.success("API key generated successfully");
    } catch (error) {
      toast.error("Failed to generate API key");
    }
  };
  
  const deactivateKey = async (keyId) => {
    if (!window.confirm("Are you sure you want to deactivate this API key?")) return;
    
    try {
      await axios.put(
        `${API_URL}/lcl-status/api-keys/${keyId}/deactivate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchApiKeys();
      toast.success("API key deactivated");
    } catch (error) {
      toast.error("Failed to deactivate API key");
    }
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const baseUrl = API_URL;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 py-8 pb-96">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <Code className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">API Integration Hub</h1>
              <p className="text-blue-100">Connect external systems to myEuropacific</p>
            </div>
          </div>
          
          {stats && (
            <div className="grid grid-cols-4 gap-4 mt-6">
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-blue-200 text-sm">Linked PFs</p>
                <p className="text-2xl font-bold">{stats.linked_pfs}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-blue-200 text-sm">Queue Pending</p>
                <p className="text-2xl font-bold">{stats.queue?.pending || 0}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-blue-200 text-sm">Status Updates</p>
                <p className="text-2xl font-bold">{stats.total_status_updates}</p>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-blue-200 text-sm">Recent (7d)</p>
                <p className="text-2xl font-bold">{stats.recent_updates_7d}</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("documentation")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "documentation" 
                ? "bg-blue-600 text-white" 
                : "bg-white text-slate-600 hover:bg-slate-100"
            }`}
          >
            <FileJson className="h-4 w-4 inline mr-2" />
            Documentation
          </button>
          <button
            onClick={() => setActiveTab("keys")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "keys" 
                ? "bg-blue-600 text-white" 
                : "bg-white text-slate-600 hover:bg-slate-100"
            }`}
          >
            <Key className="h-4 w-4 inline mr-2" />
            API Keys
          </button>
        </div>
        
        {activeTab === "documentation" && (
          <div className="space-y-6">
            {/* LCL Status API Section */}
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-6 text-white">
                <div className="flex items-center gap-3">
                  <Ship className="h-8 w-8" />
                  <div>
                    <h2 className="text-2xl font-bold">LCL Status API</h2>
                    <p className="text-emerald-100">Receive shipment data and status updates</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Overview */}
                <div className="prose max-w-none">
                  <h3 className="text-lg font-semibold text-slate-800 mb-3">Overview</h3>
                  <p className="text-slate-600">
                    The LCL Status API allows external freight forwarding systems to push shipment data directly into myEuropacific. 
                    Data is automatically matched to existing Position Folders (PFs) using the HBL (House Bill of Lading) number.
                    Unmatched shipments are added to a review queue for manual processing.
                  </p>
                </div>
                
                {/* Base URL */}
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">Base URL</p>
                  <div className="flex items-center gap-2">
                    <code className="bg-slate-900 text-emerald-400 px-3 py-2 rounded font-mono text-sm flex-1">
                      {baseUrl}/api/lcl-status
                    </code>
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(`${baseUrl}/api/lcl-status`)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Authentication */}
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Shield className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-amber-800">Authentication Required</h4>
                      <p className="text-amber-700 text-sm mt-1">
                        All API requests must include the <code className="bg-amber-100 px-1 rounded">X-API-Key</code> header with a valid API key.
                      </p>
                      <CodeBlock code={`X-API-Key: your_api_key_here`} />
                    </div>
                  </div>
                </div>
                
                {/* Endpoints */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800">Endpoints</h3>
                  
                  {/* POST /shipments */}
                  <ExpandableSection title="POST /shipments - New Shipment Notification" icon={Ship} defaultOpen={true}>
                    <div className="space-y-4">
                      <p className="text-slate-600">
                        Notify myEuropacific about a new shipment. The system will automatically match by HBL number 
                        or add to review queue if no match is found.
                      </p>
                      
                      <div>
                        <h5 className="font-medium text-slate-700 mb-2">Request Body</h5>
                        <CodeBlock code={`{
  "hbl_number": "FLHBL2024001",        // Required - House BL number
  "mbl_number": "FLMBL2024001",        // Optional - Master BL number
  "booking_number": "BK123456",        // Optional
  
  "origin_port": "CNSHA",              // Required - Port code
  "origin_port_name": "Shanghai",      // Optional
  "destination_port": "SIKOP",         // Required - Port code
  "destination_port_name": "Koper",    // Optional
  
  "shipper": {
    "name": "Shanghai Electronics Co", // Required
    "address": "123 Export St",        // Optional
    "country": "China",                // Optional
    "contact": "John Doe",             // Optional
    "email": "export@company.cn"       // Optional
  },
  
  "consignee": {
    "name": "EuroPacific Slovenia",    // Required
    "address": "456 Import Ave",       // Optional
    "country": "Slovenia",             // Optional
    "contact": "Jane Smith",           // Optional
    "email": "import@europacific.si"   // Optional
  },
  
  "cargo": {
    "description": "Electronic Parts", // Required
    "commodity": "Electronics",        // Optional
    "hs_code": "8542.31",             // Optional
    "weight_kg": 15000,               // Optional
    "volume_cbm": 32.5,               // Optional
    "packages": 50,                   // Optional
    "package_type": "Cartons",        // Optional
    "is_hazardous": false,            // Optional
    "un_number": null                 // Optional - for DG cargo
  },
  
  "containers": [
    {
      "container_number": "MSCU1234567",
      "container_type": "40HC",
      "seal_number": "SEAL123",
      "weight_kg": 15500
    }
  ],
  
  "vessel_name": "MSC AURORA",         // Optional
  "voyage_number": "FE401W",           // Optional
  "etd": "2026-03-10T00:00:00Z",       // Optional - ISO 8601
  "eta": "2026-04-05T00:00:00Z",       // Optional - ISO 8601
  "service_type": "FCL",               // Optional - FCL or LCL
  "incoterm": "FOB",                   // Optional
  "remarks": "Handle with care"        // Optional
}`} />
                      </div>
                      
                      <div>
                        <h5 className="font-medium text-slate-700 mb-2">Response - Matched to PF</h5>
                        <CodeBlock code={`{
  "status": "linked",
  "message": "Shipment linked to existing PF: PF-000123-26",
  "pf_id": "64abc123def456789",
  "pf_number": "PF-000123-26"
}`} />
                      </div>
                      
                      <div>
                        <h5 className="font-medium text-slate-700 mb-2">Response - No Match (Queued)</h5>
                        <CodeBlock code={`{
  "status": "queued",
  "message": "No matching PF found. Shipment added to review queue.",
  "queue_id": "64xyz789abc123456",
  "suggestions": [
    {
      "pf_id": "64abc...",
      "pf_number": "PF-000100-26",
      "match_reason": "Shipper name match: Shanghai Electronics",
      "customer": "EuroPacific"
    }
  ]
}`} />
                      </div>
                    </div>
                  </ExpandableSection>
                  
                  {/* POST /status-updates */}
                  <ExpandableSection title="POST /status-updates - Status Update" icon={RefreshCw}>
                    <div className="space-y-4">
                      <p className="text-slate-600">
                        Send status updates for a shipment (loaded at POL, departed, arrived at transshipment, ETA updates, etc.)
                      </p>
                      
                      <div>
                        <h5 className="font-medium text-slate-700 mb-2">Request Body</h5>
                        <CodeBlock code={`{
  "hbl_number": "FLHBL2024001",        // Required
  "mbl_number": "FLMBL2024001",        // Optional
  "container_number": "MSCU1234567",   // Optional
  
  "status_code": "LOADED",             // Required - See status codes below
  "status_description": "Container loaded at Shanghai Port",  // Required
  "status_datetime": "2026-03-10T14:30:00Z",  // Required - ISO 8601
  
  "location_code": "CNSHA",            // Optional
  "location_name": "Shanghai",         // Optional
  
  "vessel_name": "MSC AURORA",         // Optional
  "voyage_number": "FE401W",           // Optional
  
  "updated_etd": "2026-03-10T18:00:00Z",  // Optional - Updated ETD
  "updated_eta": "2026-04-06T00:00:00Z",  // Optional - Updated ETA
  
  "remarks": "On schedule"             // Optional
}`} />
                      </div>
                      
                      <div>
                        <h5 className="font-medium text-slate-700 mb-2">Common Status Codes</h5>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="bg-slate-50 p-2 rounded"><code>BOOKED</code> - Booking confirmed</div>
                          <div className="bg-slate-50 p-2 rounded"><code>LOADED</code> - Loaded at origin port</div>
                          <div className="bg-slate-50 p-2 rounded"><code>DEPARTED</code> - Departed from port</div>
                          <div className="bg-slate-50 p-2 rounded"><code>ARRIVED</code> - Arrived at port</div>
                          <div className="bg-slate-50 p-2 rounded"><code>TS_ARRIVED</code> - At transshipment</div>
                          <div className="bg-slate-50 p-2 rounded"><code>TS_LOADED</code> - Loaded at transshipment</div>
                          <div className="bg-slate-50 p-2 rounded"><code>TS_DEPARTED</code> - Departed transshipment</div>
                          <div className="bg-slate-50 p-2 rounded"><code>ETA_UPDATE</code> - ETA changed</div>
                          <div className="bg-slate-50 p-2 rounded"><code>DISCHARGED</code> - Discharged at destination</div>
                          <div className="bg-slate-50 p-2 rounded"><code>DELIVERED</code> - Delivered to consignee</div>
                        </div>
                      </div>
                      
                      <div>
                        <h5 className="font-medium text-slate-700 mb-2">Response</h5>
                        <CodeBlock code={`{
  "status": "updated",
  "message": "Status added to PF: PF-000123-26",
  "pf_id": "64abc123def456789",
  "pf_number": "PF-000123-26"
}`} />
                      </div>
                    </div>
                  </ExpandableSection>
                  
                  {/* POST /schedules */}
                  <ExpandableSection title="POST /schedules - Schedule Update" icon={Calendar}>
                    <div className="space-y-4">
                      <p className="text-slate-600">
                        Update vessel schedule information including transshipment details and updated ETD/ETA.
                      </p>
                      
                      <div>
                        <h5 className="font-medium text-slate-700 mb-2">Request Body</h5>
                        <CodeBlock code={`{
  "hbl_number": "FLHBL2024001",        // Required
  "mbl_number": "FLMBL2024001",        // Optional
  
  "vessel_legs": [
    {
      "leg": 1,
      "vessel_name": "MSC AURORA",
      "voyage_number": "FE401W",
      "from_port": "CNSHA",
      "to_port": "SGSIN",
      "etd": "2026-03-10T00:00:00Z",
      "eta": "2026-03-18T00:00:00Z"
    },
    {
      "leg": 2,
      "vessel_name": "MSC MAYA",
      "voyage_number": "AE205E",
      "from_port": "SGSIN",
      "to_port": "SIKOP",
      "etd": "2026-03-20T00:00:00Z",
      "eta": "2026-04-05T00:00:00Z"
    }
  ],
  
  "etd_origin": "2026-03-10T00:00:00Z",
  "eta_transshipment": "2026-03-18T00:00:00Z",
  "etd_transshipment": "2026-03-20T00:00:00Z",
  "eta_destination": "2026-04-05T00:00:00Z",
  
  "transshipment_ports": ["SGSIN"],
  
  "remarks": "Schedule confirmed"
}`} />
                      </div>
                    </div>
                  </ExpandableSection>
                </div>
                
                {/* Error Handling */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800">Error Responses</h3>
                  <div className="grid gap-3">
                    <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
                      <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-red-800">401 Unauthorized</p>
                        <p className="text-sm text-red-600">Invalid or missing API key</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
                      <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-orange-800">422 Validation Error</p>
                        <p className="text-sm text-orange-600">Missing required fields or invalid data format</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-slate-100 rounded-lg">
                      <AlertCircle className="h-5 w-5 text-slate-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-slate-800">500 Internal Server Error</p>
                        <p className="text-sm text-slate-600">Server-side error - please contact support</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Example Integration */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-slate-800">Example: cURL Request</h3>
                  <CodeBlock code={`curl -X POST "${baseUrl}/api/lcl-status/shipments" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: your_api_key_here" \\
  -d '{
    "hbl_number": "FLHBL2024001",
    "origin_port": "CNSHA",
    "destination_port": "SIKOP",
    "shipper": {"name": "Shanghai Electronics Co"},
    "consignee": {"name": "EuroPacific Slovenia"},
    "cargo": {"description": "Electronic Components"},
    "containers": [{"container_number": "MSCU1234567", "container_type": "40HC"}]
  }'`} />
                </div>
                
                {/* Contact */}
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Globe className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-800">Need Help?</h4>
                      <p className="text-blue-700 text-sm mt-1">
                        Contact us at <a href="mailto:api@europacific.com" className="underline">api@europacific.com</a> for 
                        integration support or to request additional endpoints.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === "keys" && (
          <div className="space-y-6">
            {/* Generate New Key */}
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Generate New API Key</h3>
              <div className="flex gap-3">
                <Input
                  placeholder="Key name (e.g., LCL Status Production)"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={generateApiKey} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Generate Key
                </Button>
              </div>
              
              {/* Show generated key */}
              {generatedKey && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-green-800">API Key Generated Successfully</p>
                      <p className="text-sm text-green-700 mt-1">Save this key now - it won't be shown again!</p>
                      
                      <div className="mt-3 space-y-2">
                        <div>
                          <p className="text-xs text-green-600">Key ID</p>
                          <code className="text-sm bg-white px-2 py-1 rounded border">{generatedKey.key_id}</code>
                        </div>
                        <div>
                          <p className="text-xs text-green-600">API Key</p>
                          <div className="flex items-center gap-2">
                            <code className="text-sm bg-white px-2 py-1 rounded border flex-1 font-mono">
                              {showKey ? generatedKey.api_key : "••••••••••••••••••••••••••••••••"}
                            </code>
                            <Button variant="outline" size="sm" onClick={() => setShowKey(!showKey)}>
                              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => copyToClipboard(generatedKey.api_key)}>
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Existing Keys */}
            <div className="bg-white rounded-xl border shadow-sm p-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Active API Keys</h3>
              
              {loading ? (
                <p className="text-slate-500">Loading...</p>
              ) : apiKeys.length === 0 ? (
                <p className="text-slate-500">No API keys generated yet.</p>
              ) : (
                <div className="space-y-3">
                  {apiKeys.map((key) => (
                    <div key={key.key_id} className={`flex items-center justify-between p-4 rounded-lg border ${key.is_active ? 'bg-white' : 'bg-slate-50'}`}>
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${key.is_active ? 'bg-green-100' : 'bg-slate-200'}`}>
                          {key.is_active ? <Unlock className="h-5 w-5 text-green-600" /> : <Lock className="h-5 w-5 text-slate-400" />}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{key.name}</p>
                          <p className="text-sm text-slate-500">ID: {key.key_id}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm">
                          <p className="text-slate-500">Created: {new Date(key.created_at).toLocaleDateString()}</p>
                          {key.last_used && (
                            <p className="text-slate-400">Last used: {new Date(key.last_used).toLocaleDateString()}</p>
                          )}
                        </div>
                        {key.is_active && (
                          <Button variant="outline" size="sm" onClick={() => deactivateKey(key.key_id)} className="text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
